import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SupabaseService } from './base/supabase.service';
import { environment } from 'src/environments/environment';

// CPK-4007: the quarantine upload + AV-enqueue below use the Supabase-JS storage client
// and a raw fetch — neither passes through NetworkInterceptor, so without an explicit
// deadline a stalled upload hangs the KYC stepper forever (reported on the 2nd document
// image). Bound both so a stall becomes a retryable error instead of an infinite spinner.
export const UPLOAD_TIMEOUT_MS = 25000;

// Rejects if `work` doesn't settle within UPLOAD_TIMEOUT_MS. uploadFilesWithRetry already
// retries on throw and surfaces the error UI, so a rejecting timeout is self-correcting.
export function withUploadTimeout<T>(work: Promise<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${UPLOAD_TIMEOUT_MS}ms`)),
      UPLOAD_TIMEOUT_MS,
    );
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  public supabaseClient: SupabaseClient;
  private sbs: SupabaseService = inject(SupabaseService);

  constructor() {
    this.supabaseClient = this.sbs.supabase;
  }

  /**
   *
   * @param bucket buckName
   * @param path  walks with filename
   * @param file file
   * @returns
   */
  async upload(bucket: string, path: string, file: File, waittime: number = UPLOAD_TIMEOUT_MS) {
    // Upload to quarantine bucket first
    const quarantineBucket = 'quarantine';
    try {
      // CPK-4007: bound the storage upload so a stalled transfer rejects (→ retry)
      // instead of hanging forever. On timeout the orphan upload may still land in
      // quarantine, but the retry mints a fresh path (v4) so there is no collision.
      const { data: uploadData, error: uploadError } = await withUploadTimeout(
        this.supabaseClient.storage
          .from(quarantineBucket)
          .upload(bucket + '/' + path, file, { contentType: file.type }),
        'Storage upload',
      );

      if (uploadError) {
        return { data: null, error: uploadError };
      }

      // Get the session to obtain the access token
      const { data: { session } } = await this.supabaseClient.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('No active session found');
      }

      // Trigger Lambda for antivirus scanning (best-effort, but time-bounded via abort)
      const avController = new AbortController();
      const avTimer = setTimeout(() => avController.abort(), UPLOAD_TIMEOUT_MS);
      try {
        const response = await fetch(`${environment.lambdaUrl}/enqueue_for_av`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quarantineBucket,
            quarantineFilePath: bucket + '/' + path,
            definitiveBucket: bucket,
            definitiveFilePath: path
          }),
          signal: avController.signal,
        });

        if (!response.ok) {
          console.error(`Failed to trigger AV scan: ${response.statusText}`);
        }
      } finally {
        clearTimeout(avTimer);
      }

      // CPK-4046: the AV scan + copy into the definitive bucket happens
      // asynchronously (SQS -> clamav-scanner ECS task). A fixed sleep here
      // used to just guess that it finished in time, which let the caller
      // report the upload as COMPLETED (and unblock questionnaire submit)
      // while the file was still only in quarantine, causing later 404s when
      // the definitive-bucket path was read back. Poll for real availability
      // instead, bounded by `waittime`.
      const available = await this.waitForFileInBucket(bucket, path, waittime);
      if (!available) {
        return {
          data: null,
          error: new Error('File was not available in the definitive bucket after antivirus scan'),
        };
      }

      return { data: uploadData, error: null };
    } catch (error) {
      console.error('Error during upload and scan process:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Polls the definitive bucket until the given path is readable (i.e. the
   * async antivirus scan + copy from quarantine has completed) or the
   * timeout elapses.
   */
  private async waitForFileInBucket(
    bucket: string,
    path: string,
    timeoutMs: number,
    pollIntervalMs: number = 500
  ): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      const { error } = await this.supabaseClient.storage
        .from(bucket)
        .createSignedUrl(path, 60);

      if (!error) return true;

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    return false;
  }

  /**
   *
   * @param bucket  buckName
   * @param path walks with filename
   */
  async download(bucket: string, path: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .download(path);

    return { data, error };
  }

  /**
   *
   * @param newBucket  buckName
   * @returns bucket creation data
   */
  async createBucket(newBucket: string) {
    const { data, error } = await this.supabaseClient.storage.createBucket(
      newBucket
    );
    return { data, error };
  }

  /**
   *
   * @param bucket buckName
   * @param folder walks with filename
   * @returns json with signed url
   */
  async signedUrl(bucket: string, folder: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .createSignedUrl(folder, 60);

    return { data, error };
  }

  async getBucket(nameBucket: string) {
    const { data, error } = await this.supabaseClient.storage.getBucket(
      nameBucket
    );
    return { data, error };
  }

  async getAllBucket() {
    const { data, error } = await this.supabaseClient.storage.listBuckets();

    return { data, error };
  }

  async deleteBucket(bucketName: string) {
    const { data, error } = await this.supabaseClient.storage.deleteBucket(
      bucketName
    );
    return { data, error };
  }

  async emptyBucket(bucketName: string) {
    const { data, error } = await this.supabaseClient.storage.emptyBucket(
      bucketName
    );
    return { data, error };
  }

  async listAllFilesBucket(bucketName: string, folder: string) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .list(folder, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' },
      });
    return { data, error };
  }

  async replaceExistingFile(bucketName: string, path: string, file: File) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .update(path, file, {
        cacheControl: '3600',
        // Overwrite file if it exis
        upsert: true,
      });

    return { data, error };
  }

  async DeleteFilesInBucket(bucketName: string, path: string[]) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucketName)
      .remove(path);

    return { data, error };
  }

  async CreateMultipleSignedUrls(bucket: string, paths: string[]) {
    const { data, error } = await this.supabaseClient.storage
      .from(bucket)
      .createSignedUrls(paths, 60);
    return { data, error };
  }

  /**
   * Get a public/signed URL for a file, checking both definitive and quarantine buckets
   * @param definitiveBuffetName The definitive bucket name (e.g., 'claim-documents')
   * @param path The file path (may include bucket prefix like 'claim-documents/...')
   * @param expiresIn Expiration time in seconds (default: 3600 = 1 hour)
   * @returns Signed URL or null if file doesn't exist in either bucket
   */
  async getFileUrl(
    definitiveBucketName: string,
    path: string,
    expiresIn: number = 3600
  ): Promise<{ url: string | null; error: Error | null }> {
    try {
      // Remove bucket prefix from path if it exists (e.g., 'claim-documents/path' -> 'path')
      let cleanPath = path;
      if (path.startsWith(`${definitiveBucketName}/`)) {
        cleanPath = path.substring(definitiveBucketName.length + 1);
      }

      // First, try to get the file from the definitive bucket
      const { data: definitiveData, error: definitiveError } = 
        await this.supabaseClient.storage
          .from(definitiveBucketName)
          .createSignedUrl(cleanPath, expiresIn);

      if (definitiveData?.signedUrl && !definitiveError) {
        return { url: definitiveData.signedUrl, error: null };
      }

      // If not found in definitive bucket, try quarantine bucket
      // The path in quarantine is: {definitiveBacketName}/{cleanPath}
      const quarantinePath = `${definitiveBucketName}/${cleanPath}`;
      const { data: quarantineData, error: quarantineError } = 
        await this.supabaseClient.storage
          .from('quarantine')
          .createSignedUrl(quarantinePath, expiresIn);

      if (quarantineData?.signedUrl && !quarantineError) {
        return { url: quarantineData.signedUrl, error: null };
      }

      // If not found in either bucket, return error
      return { 
        url: null, 
        error: new Error(`File not found in ${definitiveBucketName} or quarantine buckets`) 
      };
    } catch (error) {
      console.error('Error getting file URL:', error);
      return { url: null, error: error as Error };
    }
  }

  /**
   * Get multiple file URLs, checking both definitive and quarantine buckets
   * @param definitiveBucketName The definitive bucket name (e.g., 'claim-documents')
   * @param paths Array of file paths (without bucket prefix)
   * @param expiresIn Expiration time in seconds (default: 3600 = 1 hour)
   * @returns Array of signed URLs (null for files that don't exist)
   */
  async getMultipleFileUrls(
    definitiveBucketName: string,
    paths: string[],
    expiresIn: number = 3600
  ): Promise<{ urls: (string | null)[]; errors: (Error | null)[] }> {
    const urls: (string | null)[] = [];
    const errors: (Error | null)[] = [];

    for (const path of paths) {
      const { url, error } = await this.getFileUrl(definitiveBucketName, path, expiresIn);
      urls.push(url);
      errors.push(error);
    }

    return { urls, errors };
  }
}
